package one.healthium.tasks.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import ifaandroid.feature.tasks.tasks_ui_common.generated.resources.Res
import ifaandroid.feature.tasks.tasks_ui_common.generated.resources.ic_bottle
import ifaandroid.feature.tasks.tasks_ui_common.generated.resources.ic_checkmark_rounded_40
import ifaandroid.feature.tasks.tasks_ui_common.generated.resources.ic_checkmark_rounded_dark_40
import ifaandroid.feature.tasks.tasks_ui_common.generated.resources.ic_meditation_20
import ifaandroid.feature.tasks.tasks_ui_common.generated.resources.ic_pizza
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.tasks.domain.DailyTaskType
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black10
import one.healthium.ui.compose.theme.black50
import one.healthium.ui.compose.theme.styles.DailyTaskItemStyle
import one.healthium.ui.compose.utils.toColor
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun DailyTaskItem(
    modifier: Modifier = Modifier,
    task: DailyTaskModel,
    onClick: () -> Unit,
) {
    when (val dailyTaskItemStyle = AppTheme.dailyTaskStyle.itemStyle) {
        DailyTaskItemStyle.V1 -> DailyTaskItemV1(modifier, task, onClick)
        is DailyTaskItemStyle.V2 -> DailyTaskItemV2(modifier, task, dailyTaskItemStyle, onClick)
    }
}

@Composable
private fun DailyTaskItemV1(
    modifier: Modifier = Modifier,
    task: DailyTaskModel,
    onClick: () -> Unit,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .background(task.info.color.toColor(), AppTheme.shapes.medium)
            .clip(AppTheme.shapes.medium)
            .clickable { onClick() }
            .padding(horizontal = 12.dp, vertical = 9.dp)
            .testTag(task.info.name),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .size(36.dp)
                .background(black10, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                painter = painterResource(task.info.type.toIcon()),
                tint = black50,
                contentDescription = null,
            )
        }
        Spacer(Modifier.width(12.dp))
        Text(
            text = task.info.name,
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100.copy(alpha = if (task.completed) 0.5f else 1f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Spacer(Modifier.width(8.dp))
        Spacer(Modifier.weight(1f))
        Image(
            modifier = Modifier.testTag(if (task.completed) "completed" else "to_do"),
            painter = painterResource(
                if (task.completed) Res.drawable.ic_checkmark_rounded_dark_40
                else Res.drawable.ic_checkmark_rounded_40
            ),
            contentDescription = null,
        )
    }
}

@Composable
private fun DailyTaskItemV2(
    modifier: Modifier = Modifier,
    task: DailyTaskModel,
    style: DailyTaskItemStyle.V2,
    onClick: () -> Unit,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .background(style.containerColor ?: AppTheme.colors.surface, AppTheme.shapes.medium)
            .clip(AppTheme.shapes.medium)
            .clickable { onClick() }
            .padding(horizontal = 12.dp, vertical = 9.dp)
            .testTag(task.info.name),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .size(36.dp)
                .background(task.info.color.toColor(), CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                painter = painterResource(task.info.type.toIcon()),
                tint = style.iconTint ?: black50,
                contentDescription = null,
            )
        }
        Spacer(Modifier.width(12.dp))
        Text(
            text = task.info.name,
            style = AppTheme.textStyles.secondary_17_500,
            color = style.contentColor ?: AppTheme.colors.onSurface.copy(alpha = if (task.completed) 0.5f else 1f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Spacer(Modifier.width(8.dp))
        Spacer(Modifier.weight(1f))
        Image(
            modifier = Modifier.testTag(if (task.completed) "completed" else "to_do"),
            painter = if (task.completed) painterResource(AppIcons.icCheckMarkChecked) else painterResource(AppIcons.icCheckMarkUnchecked),
            contentDescription = null,
        )
    }
}


private fun DailyTaskType.toIcon(): DrawableResource {
    return when (this) {
        DailyTaskType.LogFood -> Res.drawable.ic_pizza
        DailyTaskType.LogWater -> Res.drawable.ic_bottle
        DailyTaskType.LogWeight -> AppIcons.icFileChecked
        is DailyTaskType.ReadInsight -> Res.drawable.ic_bottle
        is DailyTaskType.DoWorkout -> Res.drawable.ic_meditation_20
        DailyTaskType.LogSymptoms -> AppIcons.icFileChecked
    }
}
